import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import enTranslations from '../locales/en.json';
import ptTranslations from '../locales/pt.json';

// Define available languages
export type Language = 'en' | 'pt';

// Define the context shape
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, replacements?: Record<string, string>) => string;
}

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Get initial language from localStorage or default to 'pt'
  const [language, setLanguage] = useState<Language>(
    () => (localStorage.getItem('language') as Language) || 'pt'
  );
  
  // Store translations
  const [translations, setTranslations] = useState<Record<string, any>>({});

  // Load translations for current language
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        // Dynamic import of the translation file based on the language
        const translationModule = await import(`../locales/${language}.json`);
        setTranslations(translationModule.default);
        
        // Save to localStorage
        localStorage.setItem('language', language);
        
        // Set document lang attribute for accessibility
        document.documentElement.setAttribute('lang', language);
      } catch (error) {
        console.error(`Failed to load translations for ${language}`, error);
        
        // Fall back to English if loading fails
        if (language !== 'en') {
          console.warn('Falling back to English translations');
          const enModule = await import('../locales/en.json');
          setTranslations(enModule.default);
        }
      }
    };

    loadTranslations();
  }, [language]);

  // Translation function
  const t = (key: string, replacements?: Record<string, string>): string => {
    // Split the key by dots (e.g., 'calendar.title' -> ['calendar', 'title'])
    const parts = key.split('.');
    let result: any = translations;
    
    // Navigate through the nested object
    for (const part of parts) {
      if (result && typeof result === 'object' && part in result) {
        result = result[part];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key; // Return the key itself as fallback
      }
    }
    
    if (typeof result === 'string') {
      // Process replacements if any
      if (replacements) {
        return Object.entries(replacements).reduce(
          (str, [key, value]) => str.replace(new RegExp(`{${key}}`, 'g'), value),
          result
        );
      }
      return result;
    }
    
    console.warn(`Translation key does not resolve to a string: ${key}`);
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;